import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { xboxApiService } from '@/services/xboxApi';
import { RefreshCw, Send, Trash2, Search as SearchIcon, Plus, CheckCircle, AlertCircle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { useToast } from '@/components/ui/toast';

// Module-level state that persists across navigation (resets on full page reload)
const inboxStateRef: React.MutableRefObject<{
  conversations: ConversationItem[];
  resolvedPeers: Set<string>;
  lastLoaded: number;
  messageCache: Map<string, { messages: MessageItem[]; lastLoaded: number }>;
  activeConversation: { xuid: string; conversationId: string; displayName: string } | null;
} | null> = { current: null } as any;

interface ConversationItem {
  conversationId: string;
  type?: string;
  participants: string[];
  lastText?: string;
  lastTimestamp?: string;
  unread?: boolean;
  displayName?: string; // resolved peer gamertag for rendering and searching
  lastMessageId?: string;
}

interface MessageItem {
  messageId: string;
  sender: string;
  text?: string;
  timestamp: string;
  imageUrl?: string;
  isDeleted?: boolean;
}

const Inbox: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [conversations, setConversations] = React.useState<ConversationItem[]>([]);
  const [activeXuid, setActiveXuid] = React.useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<MessageItem[]>([]);
  const [composerText, setComposerText] = React.useState('');
  const [search, setSearch] = React.useState('');
  // Use module-level state instead of local refs
  const messagesContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [newTo, setNewTo] = React.useState('');
  const [newText, setNewText] = React.useState('');
  const [sendingNew, setSendingNew] = React.useState(false);
  const [newOpen, setNewOpen] = React.useState(false);
  const [sendingMessage, setSendingMessage] = React.useState(false);
  const [deletingMessage, setDeletingMessage] = React.useState<string | null>(null);
  const [friends, setFriends] = React.useState<Array<{ gamertag: string; xuid: string; isOnline: boolean }>>([]);
  const [friendSearch, setFriendSearch] = React.useState('');
  const [showFriendDropdown, setShowFriendDropdown] = React.useState(false);
  const [validating, setValidating] = React.useState(false);
  const [validationResult, setValidationResult] = React.useState<{ valid: boolean; reason?: string } | null>(null);
  const [activeDisplayName, setActiveDisplayName] = React.useState<string>('');
  const pollingIntervalRef = React.useRef<number | null>(null);
  const { toast, Toaster } = useToast();

  // XUID → Gamertag cache with 1-day expiry
  const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1 day
  
  const getGamertagFromCache = React.useCallback((xuid: string): string | null => {
    try {
      const cached = localStorage.getItem('xuid_gamertag_cache');
      if (!cached) return null;
      const cache = JSON.parse(cached) as Record<string, { gamertag: string; timestamp: number }>;
      const entry = cache[xuid];
      if (!entry) return null;
      if (Date.now() - entry.timestamp > CACHE_EXPIRY_MS) {
        // Expired, remove it
        delete cache[xuid];
        localStorage.setItem('xuid_gamertag_cache', JSON.stringify(cache));
        return null;
      }
      return entry.gamertag;
    } catch {
      return null;
    }
  }, [CACHE_EXPIRY_MS]);

  const setGamertagInCache = React.useCallback((xuid: string, gamertag: string) => {
    try {
      const cached = localStorage.getItem('xuid_gamertag_cache') || '{}';
      const cache = JSON.parse(cached) as Record<string, { gamertag: string; timestamp: number }>;
      cache[xuid] = { gamertag, timestamp: Date.now() };
      localStorage.setItem('xuid_gamertag_cache', JSON.stringify(cache));
    } catch {}
  }, []);

  // Safe text helper to avoid XSS; React escapes by default, but we also strip control chars
  const safeText = React.useCallback((s?: string) => {
    if (!s) return '';
    // eslint-disable-next-line no-control-regex
    return s.replace(/[\u0000-\u001F\u007F]/g, '').slice(0, 2000);
  }, []);

  const me = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('xbox_user_info') || '{}';
      const obj = JSON.parse(raw);
      return String(obj?.xid || obj?.xuid || '');
    } catch {
      return '';
    }
  }, []);

  const loadInbox = React.useCallback(async (force = false) => {
    // Check if we can use cached data (within 30 seconds)
    const now = Date.now();
    if (!force && inboxStateRef.current && (now - inboxStateRef.current.lastLoaded) < 30000) {
      setConversations(inboxStateRef.current.conversations);
      return;
    }

    setLoading(true);
    try {
      const res = await xboxApiService.getInbox();
      const data = res.data || {};
      const convs = (data.primary?.conversations || []).map((c: any) => {
        const lastParts = c.lastMessage?.contentPayload?.content?.parts || [];
        const lastTextPart = lastParts.find((p: any) => (p?.contentType || '').toLowerCase() === 'text');
        const lastImagePart = lastParts.find((p: any) => (p?.contentType || '').toLowerCase() === 'image');
        const lastText = lastTextPart?.text ? safeText(lastTextPart.text) : (lastImagePart ? '[Image]' : undefined);
        return {
          conversationId: c.conversationId,
          type: c.type,
          participants: c.participants || [],
          lastText,
          lastTimestamp: c.lastMessage?.timestamp,
          unread: !c.isRead,
          lastMessageId: String(c.lastMessage?.messageId || c.lastMessage?.clock || ''),
        } as ConversationItem;
      }) as ConversationItem[];
      
      // Initialize or update module state
      if (!inboxStateRef.current) {
        inboxStateRef.current = {
          conversations: convs,
          resolvedPeers: new Set(),
          lastLoaded: now,
          messageCache: new Map(),
          activeConversation: null,
        };
      } else {
        inboxStateRef.current.conversations = convs;
        inboxStateRef.current.lastLoaded = now;
      }
      
      setConversations(convs);
      
      // async resolve peer gamertags in batches (<=100) using new endpoint
      const meXuid = me;
      const peersAll = (convs || [])
        .map(c => (c.participants.find((p: string) => p !== meXuid) || c.participants[0] || '').toString())
        .filter(Boolean);
      // Skip peers already resolved, already labeled, or cached
      const uniquePeers = Array.from(new Set(peersAll)).filter(x => {
        if (!x) return false;
        if (inboxStateRef.current!.resolvedPeers.has(x)) return false;
        if (getGamertagFromCache(x)) return false; // Skip if cached
        const alreadyLabeled = (convs || []).some(cc => {
          const peer = (cc.participants.find((p: string) => p !== meXuid) || cc.participants[0] || '').toString();
          return peer === x && !!cc.displayName;
        });
        return !alreadyLabeled;
      });
      
      // First, apply cached gamertags to conversations
      const convsWithCache = convs.map(c => {
        const peer = (c.participants.find((p: string) => p !== meXuid) || c.participants[0] || '').toString();
        const cachedGt = getGamertagFromCache(peer);
        return cachedGt && !c.displayName ? { ...c, displayName: cachedGt } : c;
      });
      setConversations(convsWithCache);
      if (inboxStateRef.current) {
        inboxStateRef.current.conversations = convsWithCache;
      }
      (async () => {
        for (let i = 0; i < uniquePeers.length; i += 100) {
          const slice = uniquePeers.slice(i, i + 100);
          try {
            const res = await xboxApiService.resolveXuidsBatch(slice, ['Gamertag']);
            const list = res.data?.profileUsers || [];
            const updatedConvs = (inboxStateRef.current?.conversations || []).map(c => {
              const peer = (c.participants.find(p => p !== meXuid) || c.participants[0] || '').toString();
              const match = list.find((u: any) => String(u.id) === peer);
              const gt = match?.settings?.find((s: any) => s.id === 'Gamertag')?.value;
              return gt ? { ...c, displayName: gt } : c;
            });
            if (inboxStateRef.current) {
              inboxStateRef.current.conversations = updatedConvs;
            }
            setConversations(updatedConvs);
            // mark resolved and cache
            list.forEach((u: any) => {
              inboxStateRef.current!.resolvedPeers.add(String(u.id));
              const gt = u?.settings?.find((s: any) => s.id === 'Gamertag')?.value;
              if (gt) setGamertagInCache(String(u.id), gt);
            });
          } catch {}
        }
      })();
    } finally {
      setLoading(false);
    }
  }, [me, safeText, getGamertagFromCache, setGamertagInCache]);

  const openConversation = async (peerXuid: string, conversationId?: string) => {
    setActiveXuid(peerXuid);
    setActiveConversationId(conversationId || null);
    
    // Try to get display name from current conversations, cache, or resolve it
    const currentConv = conversations.find(c => {
      const peer = (c.participants.find(p => p !== me) || c.participants[0] || '').toString();
      return peer === peerXuid;
    });
    let displayName = currentConv?.displayName || getGamertagFromCache(peerXuid) || peerXuid;
    
    // If no display name and not cached, try to resolve it
    if (!currentConv?.displayName && !getGamertagFromCache(peerXuid)) {
      try {
        const res = await xboxApiService.resolveXuid(peerXuid);
        const gt = res.data?.profileUsers?.[0]?.settings?.find((s: any) => s.id === 'Gamertag')?.value;
        if (gt) {
          displayName = gt;
          setGamertagInCache(peerXuid, gt);
        }
      } catch {}
    }
    
    setActiveDisplayName(displayName);
    
    // Check message cache first (within 2 minutes)
    const cacheKey = peerXuid;
    const now = Date.now();
    const cached = inboxStateRef.current?.messageCache?.get(cacheKey);
    if (cached && (now - cached.lastLoaded) < 120000) {
      setMessages(cached.messages);
      // Scroll to bottom after messages render
      requestAnimationFrame(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
      // Update active conversation state
      if (inboxStateRef.current && conversationId) {
        inboxStateRef.current.activeConversation = { xuid: peerXuid, conversationId, displayName };
      }
      return;
    }
    
    setLoading(true);
    try {
      const res = await xboxApiService.getConversationWithUser(peerXuid, 100);
      const data = res.data || {};
      const msgs = (data.messages || []).map((m: any) => {
        const parts: any[] = (m?.contentPayload?.content?.parts || []) as any[];
        const textPart = parts.find(p => (p?.contentType || '').toLowerCase() === 'text');
        const imagePart = parts.find(p => (p?.contentType || '').toLowerCase() === 'image');
        const msg: MessageItem = {
          messageId: String(m.messageId || m.clock || ''),
          sender: String(m.sender || ''),
          text: textPart?.text ? safeText(textPart.text) : undefined,
          timestamp: m.timestamp,
          imageUrl: imagePart?.downloadUri,
          isDeleted: !!m.isDeleted,
        };
        return msg;
      }) as MessageItem[];
      setMessages(msgs);
      
      // Cache the messages
      if (inboxStateRef.current) {
        inboxStateRef.current.messageCache.set(cacheKey, { messages: msgs, lastLoaded: now });
        inboxStateRef.current.activeConversation = { 
          xuid: peerXuid, 
          conversationId: data.conversationId || conversationId || '', 
          displayName 
        };
      }
      
      // Scroll to bottom after messages render
      requestAnimationFrame(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
      const convId = data.conversationId || conversationId || null;
      setActiveConversationId(convId);
      // Mark read at latest message
      if (convId && msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        await xboxApiService.markConversationRead(convId, last.messageId);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!activeXuid || !composerText.trim()) return;
    const text = composerText.trim();
    setSendingMessage(true);
    try {
      const res = await xboxApiService.sendMessageToUser(activeXuid, text);
      if (res.error) {
        toast(`Failed to send: ${res.error}`, 'error');
        return;
      }
      if (res.data && res.data.messageId) {
        setComposerText('');
        const newMsg = { messageId: res.data.messageId, sender: me, text, timestamp: new Date().toISOString() };
        setMessages(prev => {
          const updated = [...prev, newMsg];
          // Update message cache
          if (inboxStateRef.current && activeXuid) {
            inboxStateRef.current.messageCache.set(activeXuid, { messages: updated, lastLoaded: Date.now() });
          }
          return updated;
        });
        if (res.data.conversationId) setActiveConversationId(res.data.conversationId);
        toast('Message sent', 'success');
      } else {
        toast('Message send failed', 'error');
      }
    } catch (error) {
      toast('Network error while sending', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const deleteMessage = async (msg: MessageItem) => {
    if (!activeConversationId) return;
    const mid = msg.messageId;
    setDeletingMessage(mid);
    const conv = activeConversationId;
    try {
      const res = await xboxApiService.deleteMessage(conv, mid);
      if (res.error) {
        toast(`Delete failed: ${res.error}`, 'error');
        return;
      }
      setMessages(prev => {
        const updated = prev.map(m => m.messageId === mid ? { ...m, isDeleted: true } : m);
        // Update message cache
        if (inboxStateRef.current && activeXuid) {
          inboxStateRef.current.messageCache.set(activeXuid, { messages: updated, lastLoaded: Date.now() });
        }
        return updated;
      });
      toast('Message deleted', 'success');
    } catch (error) {
      toast('Network error while deleting', 'error');
    } finally {
      setDeletingMessage(null);
    }
  };

  const refreshActiveConversation = React.useCallback(async () => {
    if (!activeXuid) return;
    try {
      const res = await xboxApiService.getConversationWithUser(activeXuid, 100);
      const data = res.data || {};
      const msgs = (data.messages || []).map((m: any) => {
        const parts: any[] = (m?.contentPayload?.content?.parts || []) as any[];
        const textPart = parts.find(p => (p?.contentType || '').toLowerCase() === 'text');
        const imagePart = parts.find(p => (p?.contentType || '').toLowerCase() === 'image');
        const msg: MessageItem = {
          messageId: String(m.messageId || m.clock || ''),
          sender: String(m.sender || ''),
          text: textPart?.text ? safeText(textPart.text) : undefined,
          timestamp: m.timestamp,
          imageUrl: imagePart?.downloadUri,
          isDeleted: !!m.isDeleted,
        };
        return msg;
      }) as MessageItem[];
      
      // Merge with current messages to preserve local delete states
      setMessages(current => {
        const merged = msgs.map(newMsg => {
          const existing = current.find(m => m.messageId === newMsg.messageId);
          // If we have a local delete state, preserve it unless API shows deleted
          if (existing?.isDeleted && !newMsg.isDeleted) {
            return { ...newMsg, isDeleted: true };
          }
          return newMsg;
        });
        
        // Only update if there are actual changes
        const hasChanges = merged.length !== current.length || 
          merged.some((m, i) => !current[i] || m.messageId !== current[i].messageId || m.isDeleted !== current[i].isDeleted);
        
        if (hasChanges) {
          // Update cache
          if (inboxStateRef.current) {
            inboxStateRef.current.messageCache.set(activeXuid, { messages: merged, lastLoaded: Date.now() });
          }
          // Scroll to bottom for new messages
          requestAnimationFrame(() => {
            const el = messagesContainerRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          });
          return merged;
        }
        
        return current;
      });
    } catch (error) {
      console.error('Failed to refresh conversation:', error);
    }
  }, [activeXuid, safeText]);

  const deleteConversation = async (c: ConversationItem) => {
    try {
      const convId = c.conversationId;
      const horizon = c.lastMessageId || '0';
      const convType = (c.type || '').toLowerCase() === 'group' ? 'Group' : 'OneToOne';
      const res = await xboxApiService.deleteConversationToHorizon(convId, horizon, convType as any);
      if (res.error) {
        toast(`Delete conversation failed: ${res.error}`, 'error');
        return;
      }
      const updatedConvs = conversations.filter(x => x.conversationId !== convId);
      setConversations(updatedConvs);
      // Update module state
      if (inboxStateRef.current) {
        inboxStateRef.current.conversations = updatedConvs;
      }
      if (activeConversationId === convId) {
        setActiveConversationId(null);
        setActiveXuid(null);
        setMessages([]);
      }
      toast('Conversation deleted', 'success');
    } catch (error) {
      toast('Network error while deleting conversation', 'error');
    }
  };

  // Load friends for new message selector
  React.useEffect(() => {
    const loadFriends = () => {
      try {
        const cached = localStorage.getItem('friends_cache');
        if (!cached) return;
        const parsed = JSON.parse(cached) as Array<any>;
        const list = parsed
          .filter(p => p && (p.xuid || p.gamertag || p.displayName))
          .map(p => ({
            gamertag: p.gamertag || p.displayName,
            xuid: String(p.xuid || ''),
            isOnline: ((p.presenceState || '').toLowerCase() === 'online') || !!p.isOnline,
          }))
          .sort((a, b) => Number(b.isOnline) - Number(a.isOnline) || a.gamertag.localeCompare(b.gamertag));
        setFriends(list);
      } catch {}
    };
    loadFriends();
    const interval = setInterval(loadFriends, 10000);
    return () => clearInterval(interval);
  }, []);

  // Load from cache on mount, or fetch if stale/missing
  React.useEffect(() => {
    if (inboxStateRef.current && (Date.now() - inboxStateRef.current.lastLoaded) < 30000) {
      // Use cached data
      setConversations(inboxStateRef.current.conversations);
      
      // Restore active conversation if it exists
      const active = inboxStateRef.current.activeConversation;
      if (active) {
        setActiveXuid(active.xuid);
        setActiveConversationId(active.conversationId);
        setActiveDisplayName(active.displayName);
        
        // Restore cached messages
        const cached = inboxStateRef.current.messageCache.get(active.xuid);
        if (cached && (Date.now() - cached.lastLoaded) < 120000) {
          setMessages(cached.messages);
          // Scroll to bottom
          requestAnimationFrame(() => {
            const el = messagesContainerRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          });
        }
      }
    } else {
      // Load fresh data
      loadInbox();
    }
  }, [loadInbox]);

  // Keep view pinned to bottom on message changes
  React.useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeConversationId]);

  // Auto-polling for active conversation (5 seconds)
  React.useEffect(() => {
    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Start polling if we have an active conversation
    if (activeXuid && activeConversationId) {
      pollingIntervalRef.current = window.setInterval(() => {
        // Only poll if tab is visible to avoid unnecessary API calls
        if (!document.hidden) {
          refreshActiveConversation();
        }
      }, 5000);
    }

    // Cleanup on unmount or conversation change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [activeXuid, activeConversationId, refreshActiveConversation]);

  const filteredConversations = React.useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter(c => {
      const meXuid = me;
      const peerXuid = c.participants.find(p => p !== meXuid) || c.participants[0] || '';
      const label = (c.displayName || peerXuid).toString().toLowerCase();
      const last = (c.lastText || '').toLowerCase();
      return label.includes(q) || last.includes(q);
    });
  }, [conversations, search, me]);

  return (
    <div className="space-y-6">
      <Toaster />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Inbox</h1>
          <p className="text-muted-foreground mt-2">View and send Xbox messages</p>
        </div>
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
          <Button onClick={() => setNewOpen(true)} className="flex items-center gap-2" variant="secondary">
            <Plus className="h-4 w-4" />
            New
          </Button>
          <Button 
          onClick={async () => {
            // Force reload conversations
            await loadInbox(true);
            // If actively viewing a conversation, also refresh its messages
            if (activeXuid) {
              await refreshActiveConversation();
            }
          }} 
          disabled={loading} 
          className="flex items-center gap-2"
        >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* New message modal */}
      <Modal open={newOpen} onClose={() => { setNewOpen(false); setNewTo(''); setNewText(''); setFriendSearch(''); setShowFriendDropdown(false); setValidationResult(null); }} title="New message">
        <div className="space-y-3">
          <div className="grid gap-2">
            <label className="text-xs text-muted-foreground">Recipient</label>
            <div className="relative">
              <input
                value={newTo}
                onChange={(e) => {
                  setNewTo(e.target.value);
                  setValidationResult(null);
                  setFriendSearch(e.target.value);
                  setShowFriendDropdown(e.target.value.length > 0);
                }}
                onFocus={() => {
                  setFriendSearch(newTo);
                  setShowFriendDropdown(true);
                }}
                placeholder="Type gamertag or select from friends"
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {showFriendDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-10 max-h-48 overflow-auto">
                  {friends
                    .filter(f => !friendSearch || f.gamertag.toLowerCase().includes(friendSearch.toLowerCase()))
                    .slice(0, 10)
                    .map((f) => (
                      <button
                        key={f.xuid}
                        onClick={() => {
                          setNewTo(f.gamertag);
                          setShowFriendDropdown(false);
                          setValidationResult({ valid: true });
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-accent/50 flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <span className={`inline-block h-2 w-2 rounded-full ${f.isOnline ? 'bg-green-500' : 'bg-zinc-600'}`} />
                          {f.gamertag}
                        </span>
                        <span className="text-xs text-muted-foreground">{f.isOnline ? 'Online' : 'Offline'}</span>
                      </button>
                    ))}
                  {friendSearch && !friends.some(f => f.gamertag.toLowerCase().includes(friendSearch.toLowerCase())) && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No friends match "{friendSearch}"
                    </div>
                  )}
                </div>
              )}
            </div>
            {validationResult && (
              <div className={`flex items-center gap-2 text-xs ${validationResult.valid ? 'text-green-500' : 'text-red-500'}`}>
                {validationResult.valid ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {validationResult.valid ? 'Valid gamertag' : (validationResult.reason || 'Invalid gamertag')}
              </div>
            )}
          </div>
          <div className="grid gap-2">
            <label className="text-xs text-muted-foreground">Message</label>
            <textarea
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Type a message…"
              className="min-h-[80px] w-full rounded-md border border-border bg-background p-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!newTo.trim() || !newText.trim()) return;
                const to = newTo.trim();
                const text = newText.trim();
                
                // First validate the recipient if it's not already validated
                if (!validationResult?.valid) {
                  setValidating(true);
                  try {
                    const validation = await xboxApiService.validateGamertag(to);
                    setValidationResult(validation.data || { valid: false, reason: 'Validation failed' });
                    if (!validation.data?.valid) {
                      toast(`Invalid recipient: ${validation.data?.reason || 'Gamertag not found'}`, 'error');
                      setValidating(false);
                      return;
                    }
                  } catch (error) {
                    toast('Failed to validate recipient', 'error');
                    setValidating(false);
                    return;
                  }
                  setValidating(false);
                }
                
                setSendingNew(true);
                try {
                  const res = await xboxApiService.sendTo(to, text);
                  if (res.error) {
                    toast(`Send failed: ${res.error}`, 'error');
                    return;
                  }
                  
                  if (res.data?.conversationId) {
                    // Get conversation summary to get proper participant info
                    const summaryRes = await xboxApiService.getConversationSummary(res.data.conversationId);
                    if (summaryRes.data?.conversation) {
                      const conv = summaryRes.data.conversation;
                      const participants = conv.participants || [];
                      const peerXuid = participants.find((p: string) => p !== me) || participants[0] || '';
                      
                      // Open the conversation immediately
                      if (peerXuid) {
                        setNewText('');
                        setNewTo('');
                        setNewOpen(false);
                        setValidationResult(null);
                        setShowFriendDropdown(false);
                        toast(`Message sent to ${to}`, 'success');
                        
                        // Open the conversation directly and reload inbox list
                        await openConversation(peerXuid, res.data.conversationId);
                        // Always reload inbox to show the new/updated conversation in the list
                        loadInbox(true);
                        return;
                      }
                    }
                  }
                  
                  // Fallback: just refresh inbox
                  setNewText('');
                  setNewTo('');
                  setNewOpen(false);
                  setValidationResult(null);
                  setShowFriendDropdown(false);
                  toast(`Message sent to ${to}`, 'success');
                  loadInbox(true);
                } catch (error) {
                  toast('Network error while sending', 'error');
                } finally {
                  setSendingNew(false);
                }
              }}
              disabled={validating || sendingNew || !newTo.trim() || !newText.trim()}
              className="flex items-center gap-2"
            >
              {validating ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Validating...
                </>
              ) : sendingNew ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Conversations</CardTitle>
            <CardDescription>Your latest threads</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by gamertag or message..."
                  className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
            </div>
            <div className="divide-y divide-border rounded-md border border-border max-h-[420px] overflow-auto">
              {conversations.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">No conversations</div>
              )}
              {filteredConversations.map((c) => {
                const peer = (c.participants.find(p => p !== me) || c.participants[0] || '').toString();
                const label = c.displayName || peer;
                return (
                  <div key={c.conversationId} className={`px-3 py-3 hover:bg-accent/50 ${activeConversationId === c.conversationId ? 'bg-accent/20' : ''}`}>
                    <div className="flex items-center justify-between gap-3">
                      <button 
                        onClick={() => openConversation(peer, c.conversationId)} 
                        className={`flex-1 text-left ${activeConversationId === c.conversationId ? 'bg-accent/30' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-foreground truncate" title={label as string}>{label}</div>
                          {c.unread && <span className="text-xs rounded-full bg-primary/20 text-primary px-2 py-0.5">New</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{safeText(c.lastText) || '—'}</div>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteConversation(c); }}
                        title="Delete conversation"
                        className="inline-flex items-center text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Conversation</CardTitle>
            <CardDescription>{activeXuid ? `Chat with ${activeDisplayName || activeXuid}` : 'Select a conversation'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div ref={messagesContainerRef} className="h-[55dvh] min-h-72 max-h-[520px] overflow-auto rounded-md border border-border bg-background p-3 space-y-3 lg:h-[420px]">
              {messages.length === 0 ? (
                <div className="text-sm text-muted-foreground">No messages</div>
              ) : (
                messages.map((m) => {
                  const mine = String(m.sender) === String(me);
                  const isDeleted = !!m.isDeleted;
                  return (
                    <div key={m.messageId} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] rounded-md px-3 py-2 text-sm sm:max-w-[75%] ${isDeleted ? 'opacity-50 bg-muted/40 text-muted-foreground' : mine ? 'bg-primary text-primary-foreground' : 'bg-accent/40 text-foreground'}`}>
                        {isDeleted ? (
                          <div className="space-y-1">
                            <div className="text-xs italic text-muted-foreground">Deleted message:</div>
                            {m.imageUrl ? (
                              <div className="opacity-50">
                                <img
                                  src={m.imageUrl}
                                  alt="deleted attachment"
                                  className="max-h-32 rounded-md border border-border object-cover grayscale"
                                  loading="lazy"
                                />
                                <div className="mt-1 text-[10px] line-through">Image attachment</div>
                              </div>
                            ) : (
                              <div className="whitespace-pre-wrap break-words line-through text-muted-foreground">{m.text || 'No content'}</div>
                            )}
                          </div>
                        ) : m.imageUrl ? (
                          <a href={m.imageUrl} target="_blank" rel="noreferrer" className="block group">
                            <img
                              src={m.imageUrl}
                              alt="attachment"
                              className="max-h-40 rounded-md border border-border object-cover group-hover:opacity-95"
                              loading="lazy"
                            />
                            <div className="mt-1 text-[10px] opacity-70">Open image in new tab</div>
                          </a>
                        ) : (
                          <div className="whitespace-pre-wrap break-words">{m.text || ' '}</div>
                        )}
                        <div className="mt-1 text-[10px] opacity-70">{new Date(m.timestamp).toLocaleString()}</div>
                        {!isDeleted && (
                          <div className="mt-2 flex justify-end">
                            <button 
                              className="inline-flex items-center gap-1 text-xs opacity-80 hover:opacity-100 disabled:opacity-40" 
                              onClick={() => deleteMessage(m)} 
                              title="Delete message"
                              disabled={deletingMessage === m.messageId}
                            >
                              {deletingMessage === m.messageId ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                placeholder={activeXuid ? 'Type a message…' : 'Select a conversation first'}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button onClick={sendMessage} disabled={sendingMessage || !activeXuid || !composerText.trim()} className="flex w-full items-center justify-center gap-2 sm:w-auto">
                {sendingMessage ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Inbox;


